import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Chip,
  IconButton,
  Alert,
  Grid,
  Paper,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  TableRestaurant as TableRestaurantIcon,
  PersonAdd as PersonAddIcon,
  CallSplit as CallSplitIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  DownloadForOffline as DownloadIcon,
  // AutoFixHigh as AutoFixIcon, // Removed — Distribute Equally button removed
  CheckCircleOutline as CheckCircleOutlineIcon,
} from '@mui/icons-material';

import { SplitTableService } from '../services/splitTableService';
import { SplitTableSession, SplitTablePerson, SplitTableItem, SplitPayment } from '../types/splitTable';
import { useLanguage } from '../contexts/LanguageContext';
import SplitTableDraftService from '../services/splitTableDraftService';
import { firestorePrintService } from '../services/firestorePrintService';
import { markTableSplitPaid } from '../services/firebaseTableService';

// Palette for person avatars (cycles)
const PERSON_COLORS = ['#4caf50','#2196f3','#ff9800','#9c27b0','#f44336','#00bcd4','#8bc34a','#ff5722'];
const getPersonColor = (index: number) => PERSON_COLORS[index % PERSON_COLORS.length];

interface SplitTableDialogProps {
  open: boolean;
  onClose: () => void;
  tableId: number;
  tableNumber: string;
  orderId: string;
  orderTotal: number;
  orderItems: any[];
  staffMember: string;
  draftData?: any; // Optional draft data to resume from
}

const SplitTableDialog: React.FC<SplitTableDialogProps> = ({
  open,
  onClose,
  tableId,
  tableNumber,
  orderId,
  orderTotal,
  orderItems,
  staffMember,
  draftData
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [session, setSession] = useState<SplitTableSession | null>(null);
  const [people, setPeople] = useState<SplitTablePerson[]>([]);
  const [selectedItems, setSelectedItems] = useState<SplitTableItem[]>([]);
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [customTip, setCustomTip] = useState<boolean>(false);
  const [customAmounts, setCustomAmounts] = useState<{ [personId: string]: number }>({});
  const [paymentMethods, setPaymentMethods] = useState<{ [personId: string]: string }>({});
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showAddPersonInput, setShowAddPersonInput] = useState(false);
  const [newPersonName, setNewPersonName] = useState('');
  // Item-to-person assignment map: itemKey -> personId
  const [itemAssignments, setItemAssignments] = useState<Record<string, string>>({});
  // Items fetched directly from Firestore when draft restore or prop has no items
  const [fetchedItems, setFetchedItems] = useState<any[]>([]);
  // Centered alert dialog (replaces browser alert())
  const [splitAlert, setSplitAlert] = useState<{ open: boolean; title: string; message: string; icon: string; severity: 'success' | 'error' | 'info'; onClose?: () => void } | null>(null);
  // Resolved order ID — either from prop or fetched from table document
  const resolvedOrderIdRef = useRef<string>('');
  // Prevents auto-save from firing during split completion (would create a new draft after the old one is cleared)
  const isCompletingRef = useRef(false);

  const splitService = SplitTableService.getInstance();
  const draftService = SplitTableDraftService.getInstance();
  const { t, language } = useLanguage();

  const steps = [t('split.step_create'), t('split.step_assign'), t('split.step_payment'), t('split.step_complete')];

  // Fetch order items from Firestore by order ID or order number
  const fetchItemsFromFirestore = useCallback(async (oId: string, tableNum?: string, orderNum?: string) => {
    try {
      const { getDoc, doc, collection, query, where, getDocs } = await import('../services/firestoreCompat');
      const { db } = await import('../services/firebaseConfig');

      // Try direct doc fetch by order ID
      if (oId) {
        const snap = await getDoc(doc(db, 'orders', oId));
        if (snap.exists()) {
          const d = snap.data();
          const items = d.items || [];
          if (items.length > 0) {
            console.log(`✅ [SPLIT] Fetched ${items.length} items from Firestore by orderId`);
            setFetchedItems(items);
            return;
          }
        }
      }

      // Try by order_number
      if (orderNum) {
        for (const val of [orderNum, parseInt(orderNum, 10)]) {
          const snap = await getDocs(query(collection(db, 'orders'), where('order_number', '==', val)));
          if (!snap.empty) {
            const items = snap.docs[0].data().items || [];
            if (items.length > 0) {
              console.log(`✅ [SPLIT] Fetched ${items.length} items from Firestore by order_number`);
              setFetchedItems(items);
              return;
            }
          }
        }
      }

      // Try by table_number — only include active orders.
      // Server-side status filter + positive allow-list defence prevents
      // ghost orders from previous sessions (see pos-order-status-filter-rule.md).
      if (tableNum) {
        // Use 12h cutoff to avoid loading ancient stale orders
        const cutoff = new Date(Date.now() - 12 * 60 * 60 * 1000);
        const SPLIT_ACTIVE_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'assigned'];
        for (const val of [tableNum, parseInt(tableNum, 10)]) {
          const snap = await getDocs(query(
            collection(db, 'orders'),
            where('table_number', '==', val),
            where('order_type', '==', 'dine_in'),
            where('status', 'in', SPLIT_ACTIVE_STATUSES),
          ));
          if (!snap.empty) {
            const allItems: any[] = [];
            snap.docs.forEach(d => {
              const data = d.data();
              const s = (data.status || '').toLowerCase();
              // Defence-in-depth — status already filtered server-side.
              if (!SPLIT_ACTIVE_STATUSES.includes(s)) return;
              if (data.is_split_payment) return;
              // Check order age
              let orderDate: Date | null = null;
              if (data.created_at?.toDate) orderDate = data.created_at.toDate();
              else if (data.created_at?.seconds) orderDate = new Date(data.created_at.seconds * 1000);
              else if (typeof data.created_at === 'string') orderDate = new Date(data.created_at);
              if (orderDate && orderDate < cutoff) return;
              (data.items || []).forEach((i: any) => allItems.push(i));
            });
            if (allItems.length > 0) {
              console.log(`✅ [SPLIT] Fetched ${allItems.length} items from Firestore by table_number (filtered)`);
              setFetchedItems(allItems);
              return;
            }
          }
        }
      }
      console.warn('⚠️ [SPLIT] fetchItemsFromFirestore: no items found for orderId:', oId, 'tableNum:', tableNum);
    } catch (err) {
      console.error('❌ [SPLIT] fetchItemsFromFirestore error:', err);
    }
  }, []);

  // Save draft to Firestore whenever state changes
  const saveDraftToFirestore = useCallback(async () => {
    if (!session) {
      console.log('⚠️ [DRAFT] Cannot save draft - missing session');
      return;
    }
    // Block auto-save during split completion to prevent a new draft being created
    // after the original draft is cleared, which would show duplicates in the Dashboard
    if (isCompletingRef.current) {
      console.log('⚠️ [DRAFT] Skipping auto-save — split completion in progress');
      return;
    }

    // Allow saving even if dialog is closing (open might be false)
    // This is important for the "Save as Draft & Close" functionality

    try {
      console.log('💾 [DRAFT] Preparing to save draft to Firestore...', {
        session_id: session.id,
        table_number: tableNumber,
        active_step: activeStep,
        people_count: people.length,
        status: activeStep < 3 ? 'draft' : 'pending',
        dialog_open: open
      });

      setIsSavingDraft(true);
      await draftService.saveDraft({
        session_id: session.id,
        table_id: tableId,
        table_number: tableNumber,
        order_id: orderId,
        original_order_total: orderTotal,
        status: activeStep < 3 ? 'draft' : 'pending',
        active_step: activeStep,
        people: people.map(person => {
          const personData: any = {
            id: person.id,
            name: person.name,
            items: person.items.map(item => ({
              id: item.id,
              menuItemId: item.menuItemId,
              name: item.name,
              price: item.price,
              quantity: item.quantity,
              totalPrice: item.totalPrice
            })),
            subtotal: person.subtotal || 0,
            tax: person.tax || 0,
            tip: person.tip || 0,
            total: person.total || 0,
            paymentStatus: (person.paymentStatus === 'refunded' ? 'pending' : person.paymentStatus) as 'pending' | 'partial' | 'paid',
            payments: person.payments.map(p => ({
              paymentMethod: p.paymentMethod,
              amount: p.amount,
              tip: p.tip,
              totalAmount: p.totalAmount
            }))
          };

          // Only include email/phone if they exist (don't set undefined)
          if (person.email) {
            personData.email = person.email;
          }
          if (person.phone) {
            personData.phone = person.phone;
          }

          return personData;
        }),
        selected_items: selectedItems.map(item => ({
          id: item.id,
          menuItemId: item.menuItemId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          totalPrice: item.totalPrice
        })),
        custom_amounts: customAmounts,
        payment_methods: paymentMethods,
        tip_amount: tipAmount,
        custom_tip: customTip,
        created_by: staffMember
      });
      console.log('✅ [DRAFT] Draft saved successfully to Firestore');
    } catch (error) {
      console.error('❌ [DRAFT] Error saving draft to Firestore:', error);
      console.error('❌ [DRAFT] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
    } finally {
      setIsSavingDraft(false);
    }
  }, [session, tableId, tableNumber, orderId, orderTotal, activeStep, people, selectedItems, customAmounts, paymentMethods, tipAmount, customTip, staffMember, open, draftService]);

  // Auto-save draft every 10 seconds
  useEffect(() => {
    if (!open || !session) return;

    const interval = setInterval(() => {
      saveDraftToFirestore();
    }, 10000); // Save every 10 seconds

    return () => clearInterval(interval);
  }, [open, session, saveDraftToFirestore]);

  // Save draft when state changes
  useEffect(() => {
    if (open && session) {
      const timeout = setTimeout(() => {
        saveDraftToFirestore();
      }, 2000); // Debounce: save 2 seconds after last change

      return () => clearTimeout(timeout);
    }
  }, [people, selectedItems, customAmounts, paymentMethods, tipAmount, activeStep, open, session, saveDraftToFirestore]);

  // Reset add person input when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setShowAddPersonInput(false);
      setNewPersonName('');
    }
  }, [open]);

  // Load draft data when resuming or check for existing drafts
  useEffect(() => {
    const initializeSession = async () => {
      if (!open) return;

      // If resuming from draft data
      if (draftData && !session) {
        console.log('📋 [DRAFT] Resuming from draft:', draftData);

        // Restore session
        const restoredSession = splitService.createSplitSession(
          draftData.table_id,
          draftData.table_number,
          draftData.order_id,
          draftData.original_order_total,
          draftData.created_by
        );
        setSession(restoredSession);

        // Restore people
        const restoredPeople: SplitTablePerson[] = draftData.people.map((person: any) => ({
          id: person.id,
          name: person.name,
          email: person.email,
          phone: person.phone,
          items: person.items.map((item: any) => ({
            id: item.id,
            menuItemId: item.menuItemId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            totalPrice: item.totalPrice,
            assignedTo: [],
            isShared: false,
            originalPrice: item.price,
            sharedWith: 1
          })),
          subtotal: person.subtotal || 0,
          tax: person.tax || 0,
          tip: person.tip || 0,
          total: person.total || 0,
          paymentStatus: person.paymentStatus || 'pending',
          payments: person.payments.map((p: any) => ({
            id: `temp_${Date.now()}`,
            splitId: person.id,
            paymentMethod: p.paymentMethod,
            amount: p.amount,
            tip: p.tip,
            totalAmount: p.totalAmount,
            processedBy: staffMember,
            notes: '',
            processedAt: new Date()
          }))
        }));
        setPeople(restoredPeople);

        // Restore selected items
        const restoredSelectedItems: SplitTableItem[] = draftData.selected_items.map((item: any) => ({
          id: item.id,
          menuItemId: item.menuItemId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          totalPrice: item.totalPrice,
          assignedTo: [],
          isShared: false,
          originalPrice: item.price,
          sharedWith: 1
        }));
        setSelectedItems(restoredSelectedItems);

        // If draft had no items, fetch from Firestore now
        if (restoredSelectedItems.length === 0 && orderItems.length === 0) {
          console.log('⚠️ [DRAFT] Draft has no items — fetching from Firestore:', draftData.order_id);
          fetchItemsFromFirestore(draftData.order_id || '', draftData.table_number, draftData.order_number);
        }

        // Restore custom amounts and payment methods
        setCustomAmounts(draftData.custom_amounts || {});
        setPaymentMethods(draftData.payment_methods || {});

        // Restore tip
        setTipAmount(draftData.tip_amount || 0);
        setCustomTip(draftData.custom_tip || false);

        // Restore active step
        setActiveStep(draftData.active_step || 0);

        console.log('✅ [DRAFT] Draft restored successfully');
        return;
      }

      // If creating new session, check for existing active draft for this table
      if (!session && !draftData) {
        if (tableNumber) {
          // Has table number - check for existing drafts
          try {
            const existingDraft = await draftService.getActiveDraftByTable(tableNumber);
            if (existingDraft) {
              console.log('⚠️ [DRAFT] Found existing active draft for table', tableNumber, existingDraft);
              const shouldResume = window.confirm(
                language === 'ar'
                  ? `Es gibt bereits einen aktiven Entwurf für Tisch ${tableNumber}. Möchten Sie diesen fortsetzen?`
                  : `There is already an active draft for Table ${tableNumber}. Do you want to resume it?`
              );

              if (shouldResume) {
                // Resume the existing draft
                const restoredSession = splitService.createSplitSession(
                  existingDraft.table_id,
                  existingDraft.table_number,
                  existingDraft.order_id,
                  existingDraft.original_order_total,
                  existingDraft.created_by
                );
                setSession(restoredSession);

                // Restore all data from existing draft
                const restoredPeople: SplitTablePerson[] = existingDraft.people.map((person: any) => ({
                  id: person.id,
                  name: person.name,
                  email: person.email,
                  phone: person.phone,
                  items: person.items.map((item: any) => ({
                    id: item.id,
                    menuItemId: item.menuItemId,
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    totalPrice: item.totalPrice,
                    assignedTo: [],
                    isShared: false,
                    originalPrice: item.price,
                    sharedWith: 1
                  })),
                  subtotal: person.subtotal || 0,
                  tax: person.tax || 0,
                  tip: person.tip || 0,
                  total: person.total || 0,
                  paymentStatus: person.paymentStatus || 'pending',
                  payments: person.payments.map((p: any) => ({
                    id: `temp_${Date.now()}`,
                    splitId: person.id,
                    paymentMethod: p.paymentMethod,
                    amount: p.amount,
                    tip: p.tip,
                    totalAmount: p.totalAmount,
                    processedBy: staffMember,
                    notes: '',
                    processedAt: new Date()
                  }))
                }));
                setPeople(restoredPeople);
                const existingDraftItems = existingDraft.selected_items.map((item: any) => ({
                  id: item.id,
                  menuItemId: item.menuItemId,
                  name: item.name,
                  price: item.price,
                  quantity: item.quantity,
                  totalPrice: item.totalPrice,
                  assignedTo: [],
                  isShared: false,
                  originalPrice: item.price,
                  sharedWith: 1
                }));
                setSelectedItems(existingDraftItems);

                // If draft had no items AND prop orderItems is also empty, fetch from Firestore
                if (existingDraftItems.length === 0 && orderItems.length === 0) {
                  console.log('⚠️ [DRAFT] Existing draft has no items — fetching from Firestore:', existingDraft.order_id);
                  fetchItemsFromFirestore(existingDraft.order_id || '', existingDraft.table_number, undefined);
                }

                setCustomAmounts(existingDraft.custom_amounts || {});
                setPaymentMethods(existingDraft.payment_methods || {});
                setTipAmount(existingDraft.tip_amount || 0);
                setCustomTip(existingDraft.custom_tip || false);
                setActiveStep(existingDraft.active_step || 0);

                // Update session ID to match the existing draft
                if (existingDraft.id) {
                  setSession({ ...restoredSession, id: existingDraft.session_id });
                }

                console.log('✅ [DRAFT] Resumed existing draft');
              } else {
                // User chose not to resume - cancel/delete the existing draft and create new
                if (existingDraft.id) {
                  await draftService.cancelDraft(existingDraft.id);
                  console.log('🗑️ [DRAFT] Cancelled existing draft, creating new session');
                }
                const newSession = splitService.createSplitSession(tableId, tableNumber, orderId, orderTotal, staffMember);
                setSession(newSession);
              }
            } else {
              // No existing draft - create new session
              const newSession = splitService.createSplitSession(tableId, tableNumber, orderId, orderTotal, staffMember);
              setSession(newSession);
            }
          } catch (error) {
            console.error('❌ [DRAFT] Error checking for existing draft:', error);
            // On error, just create new session
            const newSession = splitService.createSplitSession(tableId, tableNumber, orderId, orderTotal, staffMember);
            setSession(newSession);
          }
        } else {
          // No table number (dashboard scenario) - create new session directly
          console.log('📋 [SPLIT] Creating new session from dashboard (no table number)');
          const newSession = splitService.createSplitSession(tableId, tableNumber || '0', orderId, orderTotal, staffMember);
          setSession(newSession);
        }
      }
    };

    initializeSession();
  }, [open, session, draftData, tableId, tableNumber, orderId, orderTotal, staffMember, language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch items from Firestore if dialog opens with empty orderItems and no fetchedItems yet
  useEffect(() => {
    if (!open) return;
    console.log('🚀 [SPLIT v62 PREALLOC] Dialog OPENED — code version confirmed');
    console.log('📦 [SPLIT] orderItems:', orderItems.length, 'fetchedItems:', fetchedItems.length, 'orderId:', orderId, 'tableNumber:', tableNumber);
    if (orderItems.length > 0 || fetchedItems.length > 0) return;
    // Items are empty — try to fetch using available identifiers
    const effectiveOrderId = orderId || (draftData?.order_id) || '';
    const effectiveTableNum = tableNumber || (draftData?.table_number) || '';
    const effectiveOrderNum = (draftData?.order_number) || '';
    if (effectiveOrderId || effectiveTableNum || effectiveOrderNum) {
      console.log('🔍 [SPLIT] Auto-fetching items from Firestore — orderId:', effectiveOrderId, 'tableNum:', effectiveTableNum);
      fetchItemsFromFirestore(effectiveOrderId, effectiveTableNum, effectiveOrderNum);
    }
  }, [open, orderItems, fetchedItems, orderId, tableNumber, draftData, fetchItemsFromFirestore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve orderId: if prop is empty, fetch from table document or query orders
  useEffect(() => {
    if (!open) return;
    if (orderId) {
      resolvedOrderIdRef.current = orderId;
      console.log('✅ [SPLIT] orderId from prop:', orderId);
      return;
    }
    // orderId prop is empty — resolve from Firestore
    (async () => {
      try {
        const { collection, query, where, getDocs, limit: fsLimit } = await import('../services/firestoreCompat');
        const { db } = await import('../services/firebaseConfig');
        const tableNum = Number(tableNumber);
        // Try 1: get active_order_id from table document
        const tSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNum)));
        for (const tDoc of tSnap.docs) {
          const tData = tDoc.data();
          if (tData.active_order_id) {
            resolvedOrderIdRef.current = tData.active_order_id;
            console.log('✅ [SPLIT] Resolved orderId from table doc:', tData.active_order_id);
            return;
          }
        }
        // Try 2: query orders by table_number
        for (const tryNum of [tableNum, String(tableNum)]) {
          const oSnap = await getDocs(query(
            collection(db, 'orders'),
            where('table_number', '==', tryNum),
            where('order_type', '==', 'dine_in'),
            fsLimit(10)
          ));
          for (const oDoc of oSnap.docs) {
            const s = (oDoc.data().status || '').toLowerCase();
            if (s !== 'completed' && s !== 'cancelled' && !oDoc.data().is_split_payment) {
              resolvedOrderIdRef.current = oDoc.id;
              console.log('✅ [SPLIT] Resolved orderId from orders query:', oDoc.id);
              return;
            }
          }
        }
        console.warn('⚠️ [SPLIT] Could not resolve orderId for table', tableNumber);
      } catch (e) {
        console.error('❌ [SPLIT] Error resolving orderId:', e);
      }
    })();
  }, [open, orderId, tableNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    console.log('🔄 Next button clicked, current step:', activeStep);

    // Add validation for each step
    if (activeStep === 0) {
      // Step 1: Create Split - ensure at least one person is added
      if (people.length === 0) {
        setSplitAlert({ open: true, title: t('split.missing_guests'), message: t('split.error_no_person') || 'Please add at least one person', icon: '👥', severity: 'info' });
        return;
      }
      console.log('✅ Step 1 validation passed, proceeding to Assign Items');
      // Initialize assignments from any pre-existing people data (draft resume)
      initializeAssignmentsFromPeople();
    } else if (activeStep === 1) {
      // Step 2: Assign Items - check itemAssignments map
      const hasAnyAssignment = Object.keys(itemAssignments).length > 0;
      if (!hasAnyAssignment) {
        setSplitAlert({ open: true, title: t('split.no_items_assigned_title'), message: t('split.error_no_items') || 'Please assign items to guests', icon: '🍽️', severity: 'info' });
        return;
      }
      // Apply assignments to people before proceeding
      applyAssignmentsToPeople();
      console.log('✅ Step 2 validation passed, proceeding to Process Payments');
    } else if (activeStep === 2) {
      // Step 3: Process Payments - ensure all people have payment method
      // Default to 'cash' if not set, or check if payment was already processed
      const allHavePaymentMethods = people.every(person => {
        const hasMethod = paymentMethods[person.id] || 'cash'; // Default to cash
        const isPaid = person.paymentStatus === 'paid';
        // Set default payment method if not set
        if (!paymentMethods[person.id] && !isPaid) {
          setPaymentMethods(prev => ({ ...prev, [person.id]: 'cash' }));
        }
        return hasMethod || isPaid;
      });
      if (!allHavePaymentMethods) {
        setSplitAlert({ open: true, title: t('split.payment_required'), message: t('split.error_no_payment') || 'Please set payment method for all guests', icon: '💳', severity: 'info' });
        return;
      }
      console.log('✅ Step 3 validation passed, proceeding to Complete');
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const addPerson = () => {
    if (!session) return;

    // Show input field instead of prompt
    setShowAddPersonInput(true);
    setNewPersonName('');
  };

  const handleAddPersonSubmit = () => {
    if (!session) return;

    const personName = newPersonName.trim();
    if (!personName) {
      setSplitAlert({ open: true, title: t('split.name_required'), message: t('split.error_person_name_required') || 'Please enter a person name', icon: '✏️', severity: 'info' });
      return;
    }

    let person: SplitTablePerson;
    try {
      person = splitService.addPersonToSplit(session.id, personName);
    } catch {
      // Service session not in memory (e.g. resumed from draft) — create person directly
      person = {
        id: `person_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: personName,
        items: [],
        subtotal: 0,
        tax: 0,
        tip: 0,
        total: 0,
        paymentStatus: 'pending',
        payments: []
      };
    }
    setPeople([...people, person]);
    setShowAddPersonInput(false);
    setNewPersonName('');
  };

  const handleAddPersonCancel = () => {
    setShowAddPersonInput(false);
    setNewPersonName('');
  };

  const removePerson = (personId: string) => {
    if (!session) return;

    const updatedPeople = people.filter(p => p.id !== personId);
    setPeople(updatedPeople);

    // Update session
    const updatedSession = { ...session, splits: updatedPeople, splitCount: updatedPeople.length };
    setSession(updatedSession);
  };

  // Compute effective items list: from cart props OR fetched from Firestore OR reconstructed from draft/people
  const getEffectiveItems = useCallback((): any[] => {
    if (orderItems && orderItems.length > 0) {
      return orderItems;
    }
    if (fetchedItems && fetchedItems.length > 0) {
      return fetchedItems;
    }
    // Reconstruct from people's assigned items (for draft resume)
    const itemsFromPeople: any[] = [];
    people.forEach(person => {
      person.items.forEach(item => {
        // Avoid duplicates for shared items
        if (!itemsFromPeople.some(i => i.name === item.name && !item.isShared)) {
          itemsFromPeople.push({
            id: item.menuItemId,
            name: item.name,
            price: item.isShared ? item.originalPrice || item.price * (item.sharedWith || 1) : item.price,
            unitPrice: item.price,
            quantity: item.quantity,
          });
        }
      });
    });
    return itemsFromPeople;
  }, [orderItems, fetchedItems, people]);

  // Apply item assignments to people state
  const applyAssignmentsToPeople = useCallback(() => {
    const effectiveItems = getEffectiveItems();
    const updatedPeople = people.map(person => {
      const assignedItems: SplitTableItem[] = [];
      Object.entries(itemAssignments).forEach(([itemKey, assignedPersonId]) => {
        const index = parseInt(itemKey.replace('item_', ''));
        const item = effectiveItems[index];
        if (!item) return;

        if (assignedPersonId === 'shared') {
          // Item is shared equally among all people
          const n = people.length || 1;
          const itemPrice = item.price || item.unitPrice || 0;
          const itemQuantity = item.quantity || 1;
          const splitPrice = (itemPrice * itemQuantity) / n;
          assignedItems.push({
            id: `item_${index}`,
            menuItemId: item.menu_item_id || item.menuItemId || item.id || `item_${index}`,
            name: item.name,
            price: splitPrice,
            quantity: 1,
            totalPrice: splitPrice,
            assignedTo: people.map(p => p.id),
            isShared: true,
            originalPrice: itemPrice,
            sharedWith: n,
          });
        } else if (assignedPersonId === person.id) {
          const itemPrice = item.price || item.unitPrice || 0;
          const itemQuantity = item.quantity || 1;
          assignedItems.push({
            id: `item_${index}`,
            menuItemId: item.menu_item_id || item.menuItemId || item.id || `item_${index}`,
            name: item.name,
            price: itemPrice,
            quantity: itemQuantity,
            totalPrice: itemPrice * itemQuantity,
            assignedTo: [person.id],
            isShared: false,
            originalPrice: itemPrice,
            sharedWith: 1,
          });
        }
      });

      // Item prices are tax-inclusive (German brutto prices)
      // Correct breakdown: netto = gross / 1.19, tax = gross - netto, total = gross
      const grossTotal = assignedItems.reduce((sum, item) => sum + item.totalPrice, 0);
      const subtotal = grossTotal / 1.19;  // netto
      const tax = grossTotal - subtotal;   // MwSt
      const total = grossTotal + (person.tip || 0);

      return {
        ...person,
        items: assignedItems,
        subtotal,
        tax,
        total
      };
    });
    setPeople(updatedPeople);
  }, [people, itemAssignments, getEffectiveItems]);

  // Distribute Equally removed per client request — each item = one guest only

  // Assign single item to a person
  const assignItemToPerson = (itemKey: string, personId: string) => {
    setItemAssignments(prev => {
      if (!personId) {
        const updated = { ...prev };
        delete updated[itemKey];
        return updated;
      }
      return { ...prev, [itemKey]: personId };
    });
  };

  // Initialize itemAssignments from existing people data when entering Step 2
  const initializeAssignmentsFromPeople = useCallback(() => {
    const effectiveItems = getEffectiveItems();
    const assignments: Record<string, string> = {};

    people.forEach(person => {
      person.items.forEach(item => {
        if (item.isShared) {
          // Shared item — find by name and mark as shared
          const matchIndex = effectiveItems.findIndex((ei, idx) =>
            ei.name === item.name && assignments[`item_${idx}`] !== person.id
          );
          if (matchIndex >= 0) {
            assignments[`item_${matchIndex}`] = 'shared';
          }
        } else {
          const matchIndex = effectiveItems.findIndex((ei, idx) => {
            return ei.name === item.name && !assignments[`item_${idx}`];
          });
          if (matchIndex >= 0) {
            assignments[`item_${matchIndex}`] = person.id;
          }
        }
      });
    });

    setItemAssignments(assignments);
  }, [people, getEffectiveItems]);

  const processPayment = (personId: string) => {
    if (!session) return;

    const person = people.find(p => p.id === personId);
    if (!person) return;

    // Always use item-based amount (no custom override)
    const baseAmount = (person.subtotal || 0) + (person.tax || 0);

    const personPaymentMethod = paymentMethods[person.id] || 'cash';

    const payment: Omit<SplitPayment, 'id' | 'splitId' | 'processedAt'> = {
      paymentMethod: personPaymentMethod as any,
      amount: baseAmount,
      tip: tipAmount,
      totalAmount: baseAmount + tipAmount,
      processedBy: staffMember,
      notes: `Item-based split`
    };

    console.log('💳 Processing payment for', person.name, ':', payment);

    try {
      splitService.processPayment(session.id, personId, payment);
    } catch {
      // Service session not in memory (e.g. resumed from draft) — payment tracked via local state only
    }

    // Update local state
    const updatedPeople = people.map(p => {
      if (p.id === personId) {
        const totalPaid = p.payments.reduce((sum, payment) => sum + payment.totalAmount, 0) + payment.totalAmount;
        return {
          ...p,
          payments: [...p.payments, { ...payment, id: `temp_${Date.now()}`, splitId: personId, processedAt: new Date() }],
          paymentStatus: (totalPaid >= baseAmount + tipAmount ? 'paid' : 'partial') as 'paid' | 'partial'
        };
      }
      return p;
    });
    setPeople(updatedPeople);

    // Reset payment form
    setTipAmount(0);
    setCustomTip(false);
  };

  const completeSplit = async () => {
    if (!session) return;

    // CRITICAL VALIDATION: Check if ALL items are assigned to people
    const totalOrderItems = orderItems.length;
    const totalAssignedItems = people.reduce((sum, p) => sum + (p.items?.length || 0), 0);
    const unpaidPeople = people.filter(p => p.paymentStatus !== 'paid');

    if (totalAssignedItems === 0) {
      setSplitAlert({
        open: true,
        title: '⚠️ No Items Assigned',
        message: 'Cannot complete split — no items have been assigned to any guest. Please go back to Step 2 and assign items.',
        icon: '⚠️',
        severity: 'error',
      });
      return;
    }

    if (totalAssignedItems < totalOrderItems) {
      const unassignedCount = totalOrderItems - totalAssignedItems;
      const unassignedValue = orderTotal - people.reduce((sum, p) => sum + (p.subtotal || 0) + (p.tax || 0), 0);
      setSplitAlert({
        open: true,
        title: '⚠️ Unassigned Items!',
        message: `${unassignedCount} item(s) worth €${unassignedValue.toFixed(2)} are NOT assigned to any guest and will NOT be paid!\n\nUse "Partial Pay & Keep Table Open" instead to keep the table open for remaining items.\n\nOr go back to Step 2 and assign ALL items before completing.`,
        icon: '⚠️',
        severity: 'error',
      });
      return;
    }

    if (unpaidPeople.length > 0) {
      setSplitAlert({
        open: true,
        title: '⚠️ Unpaid Guests!',
        message: `${unpaidPeople.length} guest(s) (${unpaidPeople.map(p => p.name).join(', ')}) have not paid yet. Please collect payment from all guests before completing the split.`,
        icon: '⚠️',
        severity: 'error',
      });
      return;
    }

    // Stop auto-save immediately so no new draft is created during/after completion
    isCompletingRef.current = true;

    try {
      console.log('🚀🚀🚀 [SPLIT v62 PREALLOC] completeSplit STARTED — NEW CODE RUNNING');
      console.log('🎯 Completing split table session:', session.id);
      console.log('👥 People count:', people.length, 'People:', people.map(p => p.name));

      // Pre-import services once (not per-person) for speed
      const firebaseOrderService = (await import('../services/firebaseOrderService')).default;
      const orderService = firebaseOrderService.getInstance();
      const firebasePaymentService = (await import('../services/firebasePaymentService')).default;
      const paymentService = firebasePaymentService.getInstance();

      // Pre-allocate ALL order numbers in one transaction to avoid race conditions
      const orderNumbers = await orderService.getNextOrderNumbers(people.length);
      console.log('✅ Pre-allocated order numbers:', orderNumbers);

      // Fetch original order number for split receipts
      // CRITICAL: If resolvedOrderIdRef is empty, fetch active_order_id from table doc
      let origOrderNum = '';
      let origId = resolvedOrderIdRef.current || orderId || '';
      const { collection: origColl, query: origQ, where: origW, getDocs: origGD, doc: origDocRef, getDoc: origGetDoc } = await import('../services/firestoreCompat');
      const { db: origDb } = await import('../services/firebaseConfig');
      if (!origId) {
        try {
          const tableNum = Number(tableNumber);
          const tSnap = await origGD(origQ(origColl(origDb, 'restaurant_tables'), origW('number', '==', tableNum)));
          for (const td of tSnap.docs) {
            const tData = td.data();
            if (tData.active_order_id) {
              origId = tData.active_order_id;
              resolvedOrderIdRef.current = origId;
              console.log('📋 [SPLIT] Resolved origId from table doc:', origId);
              break;
            }
          }
        } catch (e) { console.warn('📋 [SPLIT] Table doc fetch failed:', e); }
      }
      let origDiscountAmount = 0;
      let origDiscountReason = '';
      let origDiscountType = '';
      let origDiscountValue = 0;
      let origTotalBeforeDiscount = 0;
      if (origId) {
        try {
          const origSnap = await origGetDoc(origDocRef(origDb, 'orders', origId));
          if (origSnap.exists()) {
            const origData = origSnap.data();
            origOrderNum = String(origData.order_number || '');
            origDiscountAmount = origData.discount_amount || 0;
            origDiscountReason = origData.discount_reason || '';
            origDiscountType = origData.discount_type || '';
            origDiscountValue = origData.discount_value || 0;
            origTotalBeforeDiscount = origData.total_before_discount || 0;
          }
        } catch { /* use allocated numbers */ }
      }
      console.log('📋 Original order number:', origOrderNum || '(not found)', 'origId:', origId || '(empty)', 'discount:', origDiscountAmount);

      // Create order entries for each person (all in parallel)
      const orderPromises = people.map(async (person, index) => {
        console.log('📝 Creating order for person:', person.name, '→ order#', orderNumbers[index]);

        // Use Firebase Order Service
        try {
          // Convert to Firebase order format
          const firebaseOrderData: any = {
            customer_name: person.name,
            customer_phone: person.phone || '',
            customer_first_name: person.name.split(' ')[0] || '',
            customer_last_name: person.name.split(' ').slice(1).join(' ') || '',
            order_type: 'dine_in',
            payment_method: paymentMethods[person.id] || 'cash',
            payment_status: 'completed',
            status: 'completed',
            subtotal: person.subtotal,
            tax: person.tax,
            total: (person.subtotal || 0) + (person.tax || 0) + tipAmount,
            table_number: parseInt(tableNumber) || null,
            items: person.items.map(item => ({
              menu_item_id: item.menuItemId || item.id,
              name: item.name,
              quantity: item.quantity,
              unit_price: item.price,
              price: item.price,
              total: item.price * item.quantity,
              notes: '',
              category_name: 'Split Item'
            })),
            notes: `Split table order - ${people.length} people sharing bill`,
            // Split payment indicators
            is_split_payment: true,
            split_session_id: session.id,
            split_person_name: person.name,
            split_total_people: people.length,
            split_person_index: people.findIndex(p => p.id === person.id) + 1,
            original_order_number: origOrderNum || '',
            original_order_id: origId || '',
            // Use original order number with person suffix (e.g. 10530-A, 10530-B)
            _preAllocatedOrderNumber: origOrderNum
              ? `${origOrderNum}-${person.name.substring(0, 10).toUpperCase()}`
              : orderNumbers[index],
          };

          // Apply proportional discount if original order had a discount
          if (origDiscountAmount > 0 && origTotalBeforeDiscount > 0) {
            const personTotal = (person.subtotal || 0) + (person.tax || 0);
            const allPeopleTotal = people.reduce((s, p) => s + (p.subtotal || 0) + (p.tax || 0), 0);
            const ratio = allPeopleTotal > 0 ? personTotal / allPeopleTotal : 1 / people.length;
            const personDiscount = Math.round(origDiscountAmount * ratio * 100) / 100;
            firebaseOrderData.discount_amount = personDiscount;
            firebaseOrderData.discount_type = origDiscountType;
            firebaseOrderData.discount_value = origDiscountValue;
            firebaseOrderData.discount_reason = origDiscountReason;
            firebaseOrderData.total_before_discount = personTotal + tipAmount;
            firebaseOrderData.total = Math.round((personTotal + tipAmount - personDiscount) * 100) / 100;
          }

          const result = await orderService.createOrder(firebaseOrderData);
          console.log('✅ Order created successfully for', person.name, result);

          // CRITICAL: Also create payment record for this split person
          try {
            const paymentData = {
              order_id: result.id || result.order_number,
              order_number: result.order_number,
              customer_name: person.name,
              payment_method: paymentMethods[person.id] || 'cash',
              amount: (person.subtotal || 0) + (person.tax || 0) + tipAmount,
              status: 'completed',
              table_number: parseInt(tableNumber) || null,
              split_details: {
                total_splits: people.length,
                splits: people.map(p => ({
                  person_name: p.name,
                  amount: (p.subtotal || 0) + (p.tax || 0) + tipAmount,
                  payment_method: paymentMethods[p.id] || 'cash',
                  items: p.items.map(item => item.name)
                }))
              },
              // Split payment indicators (for easy querying)
              is_split_payment: true,
              split_session_id: session.id,
              split_person_name: person.name,
              split_total_people: people.length,
              split_person_index: people.findIndex(p => p.id === person.id) + 1,
              processed_by: staffMember || 'POS System'
            };

            await paymentService.createPayment(paymentData);
            console.log('✅ Payment created successfully for', person.name);
          } catch (paymentError) {
            console.error('❌ Error creating payment for', person.name, paymentError);
            // Don't throw - order was created successfully, payment is secondary
          }

          return result;
        } catch (error) {
          console.error('❌ Error creating order for', person.name, error);
          throw error;
        }
      });

      await Promise.all(orderPromises);

      // Mark ALL original table orders as completed — single query, awaited
      const { collection: fsColl, query: fsQ, where: fsW, getDocs: fsGD, doc: fsDoc, updateDoc: fsUD, serverTimestamp: fsST, deleteField: fsDF } = await import('../services/firestoreCompat');
      const { db: fsDb } = await import('../services/firebaseConfig');
      try {
        const tableNumInt = parseInt(tableNumber) || 0;
        const ordersRef = fsColl(fsDb, 'orders');
        const snap = await fsGD(fsQ(ordersRef, fsW('table_number', '==', tableNumInt)));
        const updates: Promise<void>[] = [];
        for (const d of snap.docs) {
          const s = (d.data().status || '').toLowerCase();
          if (s !== 'completed' && s !== 'cancelled') {
            updates.push(fsUD(fsDoc(fsDb, 'orders', d.id), {
              status: 'completed', payment_status: 'completed',
              completed_at: fsST(), updated_at: fsST(),
            }));
          }
        }
        if (updates.length > 0) await Promise.all(updates);
      } catch (e) {
        console.warn('⚠️ Could not mark original orders as completed:', e);
      }

      // Auto-print cashier receipt via Firestore print_jobs (Pi/Windows relay)
      const splitOrderId = `SPLIT-${session.id}`;
      const splitOrderNumber = origOrderNum ? `#${origOrderNum} SPLIT` : `SPLIT-T${tableNumber}`;
      const splitOrderData: any = buildSplitOrderData();
      splitOrderData.originalOrderNumber = origOrderNum;
      splitOrderData.special_instructions = `SPLIT TABLE ${tableNumber} | ${origOrderNum ? `Order #${origOrderNum}` : ''} | ${people.map(p => `${p.name}: €${((p.subtotal||0)+(p.tax||0)).toFixed(2)}`).join(' | ')}`;
      firestorePrintService.printCashierReceipt(splitOrderId, splitOrderNumber, splitOrderData)
        .then(() => console.log('✅ Split receipt print job created'))
        .catch((e: any) => console.warn('⚠️ Split receipt print failed:', e));

      // Complete the split session (sync, non-blocking)
      try {
        splitService.completeSplitSession(session.id);
      } catch {
        // Service session not in memory (e.g. resumed from draft) — completion handled via Firestore
      }

      // Run ALL remaining Firestore operations in parallel for speed
      const parallelOps: Promise<void>[] = [];

      // Op 1: Mark table as split-paid then free it
      parallelOps.push((async () => {
        try {
          const splitTotal = people.reduce((sum, p) => sum + (p.subtotal || 0) + (p.tax || 0), 0);
          const splitPeopleData = people.map(p => ({
            name: p.name,
            total: p.total || 0,
            payment_method: paymentMethods[p.id] || 'cash',
            items: p.items.map(item => ({
              name: item.name,
              quantity: item.quantity,
              price: item.price,
              total: item.totalPrice,
              isShared: item.isShared || false,
            })),
          }));
          await markTableSplitPaid(Number(tableNumber), {
            split_session_id: session.id,
            split_people: splitPeopleData,
            split_total: splitTotal,
          });

          // Free the table — reuse already-imported firebase modules
          const tSnap = await fsGD(fsQ(fsColl(fsDb, 'restaurant_tables'), fsW('number', '==', Number(tableNumber))));
          await Promise.all(tSnap.docs.map(tDoc =>
            fsUD(tDoc.ref, {
              status: 'available',
              updated_at: new Date().toISOString(),
              updatedAt: fsST(),
              customer_name: fsDF(), total_amount: fsDF(), item_count: fsDF(),
              start_time: fsDF(), end_time: fsDF(), order_id: fsDF(),
              active_order_id: fsDF(), active_order_number: fsDF(), occupied_by: fsDF(),
              tab_open: false, tab_orders: fsDF(), tab_total: fsDF(), tab_item_count: fsDF(), tab_rounds: fsDF(),
              split_payment_complete: fsDF(), split_session_id: fsDF(), split_people: fsDF(), split_total: fsDF(),
            })
          ));
          console.log('✅ Table', tableNumber, 'freed after split completion');
        } catch (splitSaveError) {
          console.warn('⚠️ Could not mark table as split-paid:', splitSaveError);
        }
      })());

      // Op 2: Clear drafts (parallel with table update)
      parallelOps.push((async () => {
        try {
          await draftService.cancelAllDraftsForTable(tableNumber);
          console.log('✅ [DRAFT] All drafts cleared for table', tableNumber);
        } catch (draftError) {
          console.warn('⚠️ [DRAFT] Could not clear drafts:', draftError);
        }
      })());

      // Wait for all parallel ops to finish
      await Promise.all(parallelOps);

      console.log('✅ Split table completed successfully!');

      // Show success dialog, then close on OK
      setSplitAlert({
        open: true,
        title: t('split.success') || 'Split Complete',
        message: t('split.orders_sent') || 'All split orders have been created successfully.',
        icon: '✅',
        severity: 'success',
        onClose: () => { setSplitAlert(null); onClose(); }
      });
    } catch (error) {
      // Re-enable auto-save so the user can continue from where they left off
      isCompletingRef.current = false;
      console.error('❌ Error completing split table:', error);
      setSplitAlert({
        open: true,
        title: 'Error',
        message: (t('split.complete_error') || 'Failed to complete split') + ': ' + (error instanceof Error ? error.message : String(error)),
        icon: '❌',
        severity: 'error',
      });
    }
  };

  /**
   * Partial checkout: selected people pay and leave, their items are deducted from
   * the original table order, and the table stays open for remaining guests.
   */
  const partialCheckout = async () => {
    if (!session) return;

    const paidPeople = people.filter(p => p.paymentStatus === 'paid');
    if (paidPeople.length === 0) {
      setSplitAlert({ open: true, title: t('split.no_paid_guests'), message: t('split.no_paid_message'), icon: '💳', severity: 'info' });
      return;
    }

    // Block auto-save during completion
    isCompletingRef.current = true;

    try {
      console.log('🎯 [PARTIAL PAY] === STARTING PARTIAL CHECKOUT ===');
      console.log('🎯 [PARTIAL PAY] Paid guests:', paidPeople.map(p => p.name).join(', '));

      // CRITICAL: Resolve orderId NOW (not in useEffect) — must be synchronous before deduction
      let resolvedOrderId = resolvedOrderIdRef.current || orderId || '';
      if (!resolvedOrderId) {
        console.log('🔍 [PARTIAL PAY] orderId empty — resolving from Firestore NOW...');
        try {
          const { collection, query, where, getDocs, limit: fsLimit } = await import('../services/firestoreCompat');
          const { db } = await import('../services/firebaseConfig');
          const tableNum = Number(tableNumber);
          // Try table document first
          const tSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNum)));
          for (const tDoc of tSnap.docs) {
            const tData = tDoc.data();
            if (tData.active_order_id) {
              resolvedOrderId = tData.active_order_id;
              console.log('✅ [PARTIAL PAY] Resolved orderId from table doc:', resolvedOrderId);
              break;
            }
          }
          // Fallback: query orders
          if (!resolvedOrderId) {
            for (const tryNum of [tableNum, String(tableNum)]) {
              const oSnap = await getDocs(query(collection(db, 'orders'), where('table_number', '==', tryNum), where('order_type', '==', 'dine_in'), fsLimit(10)));
              for (const oDoc of oSnap.docs) {
                const s = (oDoc.data().status || '').toLowerCase();
                if (s !== 'completed' && s !== 'cancelled' && !oDoc.data().is_split_payment) {
                  resolvedOrderId = oDoc.id;
                  console.log('✅ [PARTIAL PAY] Resolved orderId from orders query:', resolvedOrderId);
                  break;
                }
              }
              if (resolvedOrderId) break;
            }
          }
        } catch (e) {
          console.error('❌ [PARTIAL PAY] Error resolving orderId:', e);
        }
      }
      resolvedOrderIdRef.current = resolvedOrderId;
      console.log('🎯 [PARTIAL PAY] FINAL orderId:', resolvedOrderId || '❌ STILL EMPTY');
      console.log('🎯 [PARTIAL PAY] tableNumber:', tableNumber);
      console.log('🎯 [PARTIAL PAY] orderTotal:', orderTotal);

      // Fetch original order number to use on split receipts
      let originalOrderNumber = '';
      if (resolvedOrderId) {
        try {
          const { doc: fsDocRef, getDoc: fsGetDoc } = await import('../services/firestoreCompat');
          const { db: fsDb2 } = await import('../services/firebaseConfig');
          const origSnap = await fsGetDoc(fsDocRef(fsDb2, 'orders', resolvedOrderId));
          if (origSnap.exists()) {
            originalOrderNumber = String(origSnap.data().order_number || '');
            console.log('✅ [PARTIAL PAY] Original order number:', originalOrderNumber);
          }
        } catch { /* fallback: will use new number */ }
      }

      // Create orders + payments for paid people
      const orderPromises = paidPeople.map(async (person) => {
        try {
          const firebaseOrderService = (await import('../services/firebaseOrderService')).default;
          const service = firebaseOrderService.getInstance();

          const firebaseOrderData = {
            customer_name: person.name,
            customer_phone: person.phone || '',
            customer_first_name: person.name.split(' ')[0] || '',
            customer_last_name: person.name.split(' ').slice(1).join(' ') || '',
            order_type: 'dine_in',
            payment_method: paymentMethods[person.id] || 'cash',
            payment_status: 'completed',
            status: 'completed',
            subtotal: person.subtotal,
            tax: person.tax,
            total: (person.subtotal || 0) + (person.tax || 0),
            table_number: parseInt(tableNumber) || null,
            items: person.items.map(item => ({
              menu_item_id: item.menuItemId || item.id,
              name: item.name,
              quantity: item.quantity,
              unit_price: item.price,
              price: item.price,
              total: item.price * item.quantity,
              notes: '',
              category_name: 'Split Item',
            })),
            notes: `Partial split — ${person.name} paid and left (Table ${tableNumber} stays open)`,
            is_split_payment: true,
            split_session_id: session.id,
            split_person_name: person.name,
            split_total_people: paidPeople.length,
            split_person_index: paidPeople.findIndex(p => p.id === person.id) + 1,
            partial_split: true,
            original_order_number: originalOrderNumber || '',
            original_order_id: resolvedOrderId || '',
            // Use original order number with person suffix
            _preAllocatedOrderNumber: originalOrderNumber
              ? `${originalOrderNumber}-${person.name.substring(0, 10).toUpperCase()}`
              : undefined,
          };

          const result = await service.createOrder(firebaseOrderData);
          console.log('✅ Partial order created for', person.name, result);

          try {
            const firebasePaymentService = (await import('../services/firebasePaymentService')).default;
            const paymentService = firebasePaymentService.getInstance();
            await paymentService.createPayment({
              order_id: result.id || result.order_number,
              order_number: result.order_number,
              customer_name: person.name,
              payment_method: paymentMethods[person.id] || 'cash',
              amount: (person.subtotal || 0) + (person.tax || 0),
              status: 'completed',
              table_number: parseInt(tableNumber) || null,
              is_split_payment: true,
              split_session_id: session.id,
              split_person_name: person.name,
              split_total_people: paidPeople.length,
              split_person_index: paidPeople.findIndex(p => p.id === person.id) + 1,
              partial_split: true,
              processed_by: staffMember || 'POS System',
            });
          } catch (payErr) {
            console.warn('⚠️ Payment record failed for', person.name, payErr);
          }

          return result;
        } catch (e) {
          console.error('❌ Order creation failed for', person.name, e);
          throw e;
        }
      });

      await Promise.all(orderPromises);

      // Deduct paid people's items from the original Firestore order
      const effectiveOrderId = resolvedOrderIdRef.current || orderId || '';
      console.log('🔍 [PARTIAL PAY] Checking orderId for deduction:', JSON.stringify(effectiveOrderId), 'resolvedRef:', resolvedOrderIdRef.current, 'prop:', orderId);
      if (effectiveOrderId) {
        console.log('✅ [PARTIAL PAY] orderId found — proceeding with item deduction');
        try {
          const { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } = await import('../services/firestoreCompat');
          const { db } = await import('../services/firebaseConfig');

          // Build deduction map by ITEM NAME (most reliable match key)
          const deductByName: Record<string, number> = {};
          for (const person of paidPeople) {
            for (const item of person.items) {
              const key = (item.name || '').trim();
              if (key) deductByName[key] = (deductByName[key] || 0) + item.quantity;
            }
          }
          console.log('🔍 [PARTIAL PAY] Deduction map (by name):', JSON.stringify(deductByName));

          const orderRef = doc(db, 'orders', effectiveOrderId);
          const orderSnap = await getDoc(orderRef);
          if (orderSnap.exists()) {
            const originalOrder = orderSnap.data();
            const originalItems: any[] = originalOrder.items || [];
            console.log('📦 [PARTIAL PAY] Original order has', originalItems.length, 'items, total:', originalOrder.total);
            originalItems.forEach((it: any, i: number) => console.log(`   [${i}] name="${it.name}", menu_item_id="${it.menu_item_id}", qty=${it.quantity}, total=${it.total}`));

            // Consume deduction budget per original item — match by NAME (reliable)
            const remaining = { ...deductByName };
            const newItems: any[] = [];
            for (const item of originalItems) {
              const key = (item.name || '').trim();
              const toDeduct = remaining[key] || 0;
              if (toDeduct <= 0) {
                newItems.push(item);
                console.log(`   ✅ KEEP: "${key}" (no deduction needed)`);
              } else {
                const newQty = item.quantity - toDeduct;
                remaining[key] = Math.max(0, toDeduct - item.quantity);
                if (newQty > 0) {
                  const unitPrice = item.unit_price || item.price || 0;
                  newItems.push({ ...item, quantity: newQty, total: Math.round(unitPrice * newQty * 100) / 100 });
                  console.log(`   ✂️ REDUCE: "${key}" from ${item.quantity} to ${newQty}`);
                } else {
                  console.log(`   🗑️ REMOVE: "${key}" (fully deducted)`);
                }
              }
            }

            // Recalculate totals — item prices are GROSS (tax included for dine-in)
            // So total = sum of item totals, and tax is EXTRACTED from total (not added on top)
            const newGrossTotal = newItems.reduce((s, it) => s + (it.total || 0), 0);
            const newTotal = Math.round(newGrossTotal * 100) / 100;
            const newTax = Math.round(newTotal * 19 / 119 * 100) / 100; // Extract 19% MwSt from gross
            const newSubtotal = Math.round((newTotal - newTax) * 100) / 100;

            // Save original total/items on FIRST deduction so complete table record is preserved
            const preserveOriginal: any = {};
            if (!originalOrder.original_total) {
              preserveOriginal.original_total = originalOrder.total || 0;
              preserveOriginal.original_items = originalItems;
              preserveOriginal.original_item_count = originalItems.length;
              console.log(`📋 [PARTIAL PAY] Preserving original total: €${preserveOriginal.original_total}`);
            }

            await updateDoc(orderRef, {
              items: newItems,
              subtotal: newSubtotal,
              tax: newTax,
              total: newTotal,
              updated_at: serverTimestamp(),
              partial_split_applied: true,
              partial_split_session_id: session.id,
              ...preserveOriginal,
            });
            console.log(`💰 [PARTIAL PAY] Order ${orderId} updated:`);
            console.log(`   📋 Before: ${originalItems.length} items`);
            console.log(`   📋 After: ${newItems.length} items`);
            console.log(`   📋 Removed: ${originalItems.length - newItems.length} items`);
            console.log(`   💵 New subtotal: €${newSubtotal.toFixed(2)}`);
            console.log(`   💵 New tax: €${newTax.toFixed(2)}`);
            console.log(`   💵 New total: €${newTotal.toFixed(2)}`);
            console.log(`   📋 Remaining items:`, newItems.map((it: any) => `${it.quantity}× ${it.menu_item_name || it.name}`));

            // CRITICAL: Update table document totals so Restaurant page shows new balance immediately
            try {
              const tableNum = Number(tableNumber);
              console.log(`🔄 [PARTIAL PAY] Updating table ${tableNum} document...`);
              const tSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNum)));
              const newItemCount = newItems.reduce((sum: number, it: any) => sum + (it.quantity || 1), 0);
              for (const tDoc of tSnap.docs) {
                const oldData = tDoc.data();
                console.log(`   📊 Table BEFORE: total=€${oldData.total_amount || oldData.tab_total || 0}, items=${oldData.item_count || oldData.tab_item_count || 0}`);
                await updateDoc(tDoc.ref, {
                  total_amount: newTotal,
                  item_count: newItemCount,
                  tab_total: newTotal,
                  tab_item_count: newItemCount,
                  updated_at: new Date().toISOString(),
                  updatedAt: serverTimestamp(),
                });
                console.log(`   📊 Table AFTER: total=€${newTotal.toFixed(2)}, items=${newItemCount}`);
              }
              console.log(`✅ [PARTIAL PAY] Table ${tableNumber} balance updated successfully`);
            } catch (tableErr) {
              console.warn('⚠️ Could not update table totals:', tableErr);
            }
          }
        } catch (deductErr) {
          console.warn('⚠️ Could not deduct items from original order:', deductErr);
        }
      } else {
        console.error('❌ [PARTIAL PAY] orderId is EMPTY — cannot deduct items from order! Table totals will NOT update.');
        console.error('❌ [PARTIAL PAY] This is the bug — orderId prop:', orderId, 'tableNumber:', tableNumber);

        // FALLBACK: Try to find the order by querying the table's active_order_id
        try {
          console.log('🔄 [PARTIAL PAY] Attempting fallback: find order from table doc...');
          const { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp } = await import('../services/firestoreCompat');
          const { db } = await import('../services/firebaseConfig');
          const tableNum = Number(tableNumber);
          const tSnap = await getDocs(query(collection(db, 'restaurant_tables'), where('number', '==', tableNum)));
          let fallbackOrderId = '';
          for (const tDoc of tSnap.docs) {
            const tData = tDoc.data();
            fallbackOrderId = tData.active_order_id || tData.order_id || '';
            console.log('🔍 [PARTIAL PAY] Table doc active_order_id:', fallbackOrderId);
          }

          if (fallbackOrderId) {
            console.log('✅ [PARTIAL PAY] Found order via fallback:', fallbackOrderId);

            // Build deduction map
            const deductMap: Record<string, number> = {};
            for (const person of paidPeople) {
              for (const item of person.items) {
                const key = item.menuItemId || item.name;
                deductMap[key] = (deductMap[key] || 0) + item.quantity;
              }
            }

            const orderRef = doc(db, 'orders', fallbackOrderId);
            const orderSnap = await getDoc(orderRef);
            if (orderSnap.exists()) {
              const originalOrder = orderSnap.data();
              const originalItems: any[] = originalOrder.items || [];

              const remaining = { ...deductMap };
              const newItems: any[] = [];
              for (const item of originalItems) {
                const key = item.menu_item_id || item.name;
                const toDeduct = remaining[key] || 0;
                if (toDeduct <= 0) {
                  newItems.push(item);
                } else {
                  const newQty = item.quantity - toDeduct;
                  remaining[key] = Math.max(0, toDeduct - item.quantity);
                  if (newQty > 0) {
                    const unitPrice = item.unit_price || item.price || 0;
                    newItems.push({ ...item, quantity: newQty, total: Math.round(unitPrice * newQty * 100) / 100 });
                  }
                }
              }

              // Item prices are GROSS (tax included) — extract tax, don't add it
              const newGrossTotal = newItems.reduce((s: number, it: any) => s + (it.total || 0), 0);
              const newTotal = Math.round(newGrossTotal * 100) / 100;
              const newTax = Math.round(newTotal * 19 / 119 * 100) / 100;
              const newSubtotal = Math.round((newTotal - newTax) * 100) / 100;

              // Preserve original total on first deduction
              const fbPreserve: any = {};
              if (!originalOrder.original_total) {
                fbPreserve.original_total = originalOrder.total || 0;
                fbPreserve.original_items = originalItems;
                fbPreserve.original_item_count = originalItems.length;
              }

              await updateDoc(orderRef, {
                items: newItems,
                subtotal: newSubtotal,
                tax: newTax,
                total: newTotal,
                updated_at: serverTimestamp(),
                partial_split_applied: true,
                ...fbPreserve,
              });

              console.log(`💰 [PARTIAL PAY FALLBACK] Order updated: ${originalItems.length} → ${newItems.length} items, total: €${newTotal.toFixed(2)}`);

              // Update table doc
              const newItemCount = newItems.reduce((sum: number, it: any) => sum + (it.quantity || 1), 0);
              for (const tDoc of tSnap.docs) {
                await updateDoc(tDoc.ref, {
                  total_amount: newTotal,
                  item_count: newItemCount,
                  tab_total: newTotal,
                  tab_item_count: newItemCount,
                  updated_at: new Date().toISOString(),
                  updatedAt: serverTimestamp(),
                });
              }
              console.log(`✅ [PARTIAL PAY FALLBACK] Table ${tableNumber} updated: €${newTotal.toFixed(2)}, ${newItemCount} items`);
            }
          } else {
            console.error('❌ [PARTIAL PAY] Fallback also failed — no order ID found on table doc');
          }
        } catch (fallbackErr) {
          console.error('❌ [PARTIAL PAY] Fallback deduction failed:', fallbackErr);
        }
      }

      // Print receipt for paid people only (NOT the full table)
      // Use original order number so all split receipts reference the same table order
      const splitOrderId = `SPLIT-PARTIAL-${session.id}`;
      const splitOrderNumber = originalOrderNumber ? `#${originalOrderNumber} SPLIT` : `SPLIT-T${tableNumber}`;
      const splitOrderData: any = buildSplitOrderData(paidPeople);
      splitOrderData.originalOrderNumber = originalOrderNumber;
      splitOrderData.special_instructions = `SPLIT TABLE ${tableNumber} | ${originalOrderNumber ? `Order #${originalOrderNumber}` : ''} | ${paidPeople.map(p => `${p.name}: €${((p.subtotal||0)+(p.tax||0)).toFixed(2)}`).join(' | ')} | Partial payment`;
      const paidTotal = paidPeople.reduce((sum, p) => sum + (p.subtotal || 0) + (p.tax || 0), 0);
      console.log(`🧾 [PARTIAL PAY] Printing receipt for ${paidPeople.length} guest(s):`);
      paidPeople.forEach(p => {
        console.log(`   👤 ${p.name}: ${p.items.length} items, €${((p.subtotal || 0) + (p.tax || 0)).toFixed(2)}, payment: ${paymentMethods[p.id] || 'cash'}`);
        p.items.forEach((it: any) => console.log(`      - ${it.quantity}× ${it.name} €${(it.price * it.quantity).toFixed(2)}`));
      });
      console.log(`   💵 Receipt total: €${paidTotal.toFixed(2)} (NOT full table bill)`);
      firestorePrintService.printCashierReceipt(splitOrderId, splitOrderNumber, splitOrderData)
        .then(() => console.log('✅ [PARTIAL PAY] Receipt printed successfully'))
        .catch((e: any) => console.warn('⚠️ Partial receipt print failed:', e));

      // Cancel all active drafts (table no longer needs a split draft)
      try {
        await draftService.cancelAllDraftsForTable(tableNumber);
      } catch (e) {
        console.warn('⚠️ Could not clear drafts:', e);
      }

      console.log('✅ Partial checkout done — table stays open');
      const names = paidPeople.map(p => p.name).join(', ');
      setSplitAlert({ open: true, title: 'Partial Checkout Complete', message: `${names} paid and left. Their items have been removed from the table bill. Table ${tableNumber} remains open for the remaining guests.`, icon: '✅', severity: 'success' });

      onClose();
    } catch (error) {
      isCompletingRef.current = false;
      console.error('❌ Partial checkout failed:', error);
      setSplitAlert({ open: true, title: 'Checkout Failed', message: 'Partial checkout failed: ' + (error instanceof Error ? error.message : String(error)), icon: '❌', severity: 'error' });
    }
  };

  /**
   * Build structured order data for Firestore print relay.
   * Pass `peopleToPrint` to limit the receipt to a subset of people (e.g. partial checkout).
   */
  const buildSplitOrderData = (peopleToPrint: SplitTablePerson[] = people) => {
    const totalAmount = peopleToPrint.reduce((sum, person) => sum + (person.subtotal || 0) + (person.tax || 0), 0);
    const sessionDate = new Date();

    const getCategoryFromItemName = (itemName: string): string => {
      const name = itemName.toLowerCase();
      if (name.includes('pizza')) return 'Pizza';
      if (name.includes('pasta') || name.includes('spaghetti') || name.includes('penne') || name.includes('maccheron')) return 'Pasta';
      if (name.includes('sushi') || name.includes('maki') || name.includes('nigiri') || name.includes('sashimi')) return 'Sushi';
      if (name.includes('salat') || name.includes('insalata')) return 'Salate';
      if (name.includes('dessert') || name.includes('tiramisu') || name.includes('gelato')) return 'Desserts';
      return 'Sonstiges';
    };

    const allItems = peopleToPrint.flatMap(person =>
      person.items.map((item: any) => ({
        name: item.name,
        quantity: item.quantity,
        price: item.price,
        total: item.price * item.quantity,
        notes: `Für: ${person.name}`,
        category_name: getCategoryFromItemName(item.name),
      }))
    );

    const splitSummary = peopleToPrint.map(p =>
      `${p.name}: €${((p.subtotal || 0) + (p.tax || 0)).toFixed(2)} (${paymentMethods[p.id] || 'cash'})`
    ).join(' | ');

    const splitPersons = peopleToPrint.map(p => ({
      name: p.name,
      items: p.items.map((item: any) => ({
        name: item.isShared ? `${item.name} (÷${item.sharedWith || peopleToPrint.length})` : item.name,
        quantity: item.quantity,
        price: item.price,
        total: item.totalPrice || item.price * item.quantity,
      })),
      subtotal: p.subtotal || 0,
      tax: p.tax || 0,
      total: (p.subtotal || 0) + (p.tax || 0),
      paymentMethod: paymentMethods[p.id] || 'cash',
    }));

    return {
      orderNumber: `SPLIT-T${tableNumber}`,
      customerName: `SPLIT TABLE ${tableNumber}`,
      customerAddress: `Aufgeteilt auf ${peopleToPrint.length} Personen`,
      items: allItems,
      subtotal: totalAmount / 1.19,
      tax: totalAmount - totalAmount / 1.19,
      total: totalAmount,
      paymentMethod: 'Split Payment',
      paymentStatus: 'Paid',
      orderDate: sessionDate.toLocaleDateString('en-US'),
      orderTime: sessionDate.toLocaleTimeString('en-US'),
      orderType: 'dine_in',
      specialInstructions: `SPLIT TABLE ${tableNumber} | ${peopleToPrint.length} Personen | ${splitSummary}`,
      is_split_payment: true,
      split_session_id: session?.id,
      table_number: tableNumber,
      split_persons: splitPersons,
    };
  };

  const printReceipt = async (type: 'customer' | 'kitchen' | 'tse') => {
    if (!session) return;

    console.log(`🖨️ Printing ${type} via Firestore relay for split table ${tableNumber}`);

    try {
      const splitOrderId = `SPLIT-${session.id}`;
      const splitOrderNumber = `SPLIT-T${tableNumber}`;
      const orderData = buildSplitOrderData();

      if (type === 'kitchen') {
        await firestorePrintService.printKitchenOrder(splitOrderId, splitOrderNumber, orderData);
        console.log('✅ Kitchen print job created via Firestore relay');
      } else {
        // customer or tse — send to both cashier printers (.45 and .46)
        await firestorePrintService.printCashierReceipt(splitOrderId, splitOrderNumber, orderData);
        console.log('✅ Cashier receipt print jobs created via Firestore relay');
      }
    } catch (error) {
      console.error('❌ Print error:', error);
      setSplitAlert({ open: true, title: 'Print Error', message: (t('split.print_error') || 'Print failed') + ': ' + error, icon: '🖨️', severity: 'error' });
    }
  };

  const exportReport = () => {
    if (!session) return;

    const totalAmount = people.reduce((sum, person) => sum + person.total, 0);
    const sessionDate = new Date().toLocaleString();

    let csvContent = `Split Table Report - Table ${tableNumber}\n`;
    csvContent += `Date,${sessionDate}\n`;
    csvContent += `Session ID,${session.id}\n`;
    csvContent += `Total Amount,€${totalAmount.toFixed(2)}\n`;
    csvContent += `Number of People,${people.length}\n`;
    csvContent += `Average per Person,€${(totalAmount / people.length).toFixed(2)}\n\n`;

    csvContent += `Person Name,Items Count,Subtotal,Tax,Tip,Total,Payment Method\n`;

    people.forEach(person => {
      csvContent += `${person.name},${person.items.length},€${(person.subtotal || 0).toFixed(2)},€${(person.tax || 0).toFixed(2)},€${(person.tip || 0).toFixed(2)},€${(person.total || 0).toFixed(2)},${person.payments[0]?.paymentMethod || 'Cash'}\n`;
    });

    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `split_table_${tableNumber}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);

    console.log('📊 Split table report exported successfully');
  };

  const renderStepContent = (step: number) => {
    // ── Shared order summary banner ──────────────────────────────────────────
    const OrderBanner = () => (
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 1.5, lg: 2.5 }, py: { xs: 0.75, lg: 1.5 }, mb: { xs: 1.5, lg: 3 }, borderRadius: 1.5,
        background: 'rgba(0, 200, 150, 0.08)',
        border: '1px solid rgba(0, 200, 150, 0.2)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: { xs: 28, lg: 36 }, height: { xs: 28, lg: 36 }, borderRadius: '50%', background: 'linear-gradient(135deg, #4caf50, #2e7d32)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TableRestaurantIcon sx={{ color: '#fff', fontSize: { xs: 14, lg: 18 } }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: '0.6rem', lg: '0.68rem' }, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
              {t('split.table')} {tableNumber}
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.8rem', lg: '1rem' }, fontWeight: 700, color: '#00C896', lineHeight: 1.2 }}>
              {t('split.split_bill') || 'Split Bill'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography sx={{ fontSize: { xs: '0.55rem', lg: '0.65rem' }, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            {t('split.original_total')}
          </Typography>
          <Typography sx={{ fontSize: { xs: '1.1rem', lg: '1.5rem' }, fontWeight: 800, color: '#00C896', lineHeight: 1 }}>
            €{orderTotal.toFixed(2)}
          </Typography>
        </Box>
      </Box>
    );

    switch (step) {
      case 0:
        return (
          <Box>
            <OrderBanner />

            {/* Add person form */}

            {/* Add person area */}
            <Box sx={{ mb: 3 }}>
              {!showAddPersonInput ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={addPerson}
                    disabled={people.length >= 8}
                    sx={{
                      background: 'linear-gradient(135deg, #4caf50, #2e7d32)',
                      color: '#ffffff !important',
                      borderRadius: 2, textTransform: 'none', fontWeight: 700,
                      px: 3, py: 1.2, minHeight: 44, touchAction: 'manipulation',
                      boxShadow: '0 2px 8px rgba(76,175,80,0.3)',
                      '&:hover': { background: 'linear-gradient(135deg, #43a047, #1b5e20)', color: '#ffffff !important' },
                      '& .MuiButton-startIcon, & .MuiButton-startIcon *': { color: '#ffffff !important' },
                    }}
                  >
                    {t('split.add_person')}
                  </Button>
                  {people.length > 0 && (
                    <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>
                      {people.length}/8 {t('split.people') || 'people'}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Paper elevation={0} sx={{ p: 2, border: '2px solid #00C896', borderRadius: 2, background: 'rgba(0, 200, 150, 0.08)' }}>
                  <Typography sx={{ fontWeight: 600, color: '#00C896', mb: 1.5, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    {t('split.add_person')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                    <TextField
                      autoFocus fullWidth
                      placeholder={t('split.person_name') || 'Enter name…'}
                      value={newPersonName}
                      onChange={(e) => setNewPersonName(e.target.value)}
                      onKeyPress={(e) => { if (e.key === 'Enter') handleAddPersonSubmit(); else if (e.key === 'Escape') handleAddPersonCancel(); }}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, backgroundColor: '#1e2139', fontWeight: 600, color: '#e0e0e0', '& fieldset': { borderColor: '#00C896' }, fontSize: '0.85rem' } }}
                    />
                    <Button variant="contained" onClick={handleAddPersonSubmit} disabled={!newPersonName.trim()}
                      sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700, minWidth: { xs: 60, md: 80 }, background: '#00C896', color: '#0A2540', fontSize: { xs: '0.75rem', md: '0.875rem' }, '&:hover': { background: '#1AD4A6' } }}>
                      {t('split.add') || 'Add'}
                    </Button>
                    <Button variant="outlined" onClick={handleAddPersonCancel}
                      sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, minWidth: { xs: 60, md: 80 }, borderColor: '#3a3d52', color: '#9ca3af', fontSize: { xs: '0.75rem', md: '0.875rem' }, '&:hover': { borderColor: '#00C896' } }}>
                      {t('split.cancel') || 'Cancel'}
                    </Button>
                  </Box>
                </Paper>
              )}
              {people.length >= 8 && (
                <Chip label="Max 8 people" color="warning" size="small" sx={{ mt: 1 }} />
              )}
            </Box>

            {/* Empty state */}
            {people.length === 0 && (
              <Box sx={{ textAlign: 'center', py: { xs: 2, lg: 6 }, color: '#9ca3af' }}>
                <CallSplitIcon sx={{ fontSize: { xs: 32, lg: 48 }, mb: 0.5, opacity: 0.4 }} />
                <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', lg: '1rem' } }}>{t('split.add_people_hint')}</Typography>
                <Typography sx={{ fontSize: { xs: '0.68rem', lg: '0.8rem' } }}>{t('split.max_people_hint')}</Typography>
              </Box>
            )}

            {/* People cards */}
            <Grid container spacing={{ xs: 1, lg: 2 }}>
              {people.map((person, idx) => (
                <Grid item xs={6} sm={4} md={4} key={person.id}>
                  <Paper elevation={0} sx={{
                    p: { xs: 1, lg: 2 }, borderRadius: 1.5, position: 'relative',
                    border: `2px solid ${getPersonColor(idx)}20`,
                    background: `linear-gradient(135deg, ${getPersonColor(idx)}08 0%, #fff 100%)`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Avatar sx={{ width: 38, height: 38, background: getPersonColor(idx), fontWeight: 700, fontSize: '1rem' }}>
                        {person.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {person.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {person.items.length} {t('split.items')} · €{(person.total || 0).toFixed(2)}
                        </Typography>
                      </Box>
                      <Tooltip title="Remove person">
                        <IconButton size="small" onClick={() => removePerson(person.id)}
                          sx={{ color: '#ef4444', '&:hover': { background: '#fef2f2' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case 1: {
        const effectiveItems = getEffectiveItems();
        // Compute per-person totals from current assignments (handle shared items)
        const personTotals: Record<string, number> = {};
        people.forEach(p => { personTotals[p.id] = 0; });
        Object.entries(itemAssignments).forEach(([itemKey, assignedId]) => {
          const idx = parseInt(itemKey.replace('item_', ''));
          const item = effectiveItems[idx];
          if (!item) return;
          const price = item.price || item.unitPrice || 0;
          const qty = item.quantity || 1;
          if (assignedId === 'shared') {
            const share = (price * qty) / (people.length || 1);
            people.forEach(p => { personTotals[p.id] += share; });
          } else if (personTotals[assignedId] !== undefined) {
            personTotals[assignedId] += price * qty;
          }
        });
        const assignedCount = Object.keys(itemAssignments).length;
        const totalItems = effectiveItems.length;

        const progress = totalItems > 0 ? (assignedCount / totalItems) * 100 : 0;
        return (
          <Box>
            <OrderBanner />

            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '0.95rem' }}>
                  {t('split.assign_items_title')}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {assignedCount} / {totalItems} {t('split.items')}
                </Typography>
              </Box>
              {/* Distribute Equally button removed per client request — each item = one guest */}
            </Box>

            {/* Progress bar */}
            <Box sx={{ mb: 2.5 }}>
              <LinearProgress
                variant="determinate" value={progress}
                sx={{
                  height: 6, borderRadius: 3,
                  backgroundColor: '#2a2d3e',
                  '& .MuiLinearProgress-bar': {
                    background: progress === 100 ? 'linear-gradient(90deg,#4caf50,#2e7d32)' : 'linear-gradient(90deg,#f59e0b,#d97706)',
                    borderRadius: 3,
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>0%</Typography>
                <Chip
                  label={progress === 100 ? `✓ ${t('split.all_assigned')}` : `${Math.round(progress)}%`}
                  size="small"
                  sx={{
                    fontSize: '0.68rem', height: 18, fontWeight: 600,
                    background: progress === 100 ? '#dcfce7' : '#fef3c7',
                    color: progress === 100 ? '#15803d' : '#92400e',
                  }}
                />
                <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>100%</Typography>
              </Box>
            </Box>

            {/* Person summary chips */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
              {people.map((person, idx) => {
                const ownCount = Object.values(itemAssignments).filter(pid => pid === person.id).length;
                const sharedCount = Object.values(itemAssignments).filter(pid => pid === 'shared').length;
                const cnt = ownCount + sharedCount;
                return (
                  <Chip key={person.id}
                    avatar={<Avatar sx={{ background: getPersonColor(idx), width: 20, height: 20, fontSize: '0.65rem !important', fontWeight: 700 }}>{person.name.charAt(0).toUpperCase()}</Avatar>}
                    label={`${person.name} · €${(personTotals[person.id] || 0).toFixed(2)}${sharedCount > 0 ? ` (${ownCount}+${sharedCount}÷${people.length})` : ownCount > 0 ? ` (${ownCount})` : ''}`}
                    size="small"
                    sx={{
                      background: cnt > 0 ? `${getPersonColor(idx)}18` : '#f3f4f6',
                      border: `1px solid ${cnt > 0 ? getPersonColor(idx) + '40' : '#e5e7eb'}`,
                      color: cnt > 0 ? getPersonColor(idx) : '#6b7280',
                      fontWeight: 600, fontSize: '0.72rem',
                    }}
                  />
                );
              })}
            </Box>

            {/* Items list */}
            <Box sx={{ mb: 1 }}>
              {effectiveItems.map((item, index) => {
                const itemKey = `item_${index}`;
                const assignedPersonId = itemAssignments[itemKey] || '';
                const isSharedAssignment = assignedPersonId === 'shared';
                const assignedPerson = isSharedAssignment ? null : people.find(p => p.id === assignedPersonId);
                const personIdx = assignedPerson ? people.indexOf(assignedPerson) : -1;
                const itemPrice = item.price || item.unitPrice || 0;
                const itemQty = item.quantity || 1;
                const sharedPrice = (itemPrice * itemQty) / (people.length || 1);
                return (
                  <Paper key={index} elevation={0} sx={{
                    p: 1.5, mb: 1,
                    border: isSharedAssignment
                      ? '1.5px solid #9c27b040'
                      : assignedPersonId
                        ? `1.5px solid ${getPersonColor(personIdx)}60`
                        : '1.5px solid #2a2d3e',
                    borderRadius: 2,
                    background: isSharedAssignment
                      ? '#f3e5f508'
                      : assignedPersonId
                        ? `${getPersonColor(personIdx)}08`
                        : '#1e2139',
                    display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontWeight: 600, color: '#e0e0e0', fontSize: { xs: '0.82rem', sm: '0.88rem' } }} noWrap>
                          {item.name}
                        </Typography>
                        {isSharedAssignment && (
                          <Chip label={`÷${people.length}`} size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#9c27b020', color: '#7b1fa2', px: 0.2 }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                        €{itemPrice.toFixed(2)} × {itemQty} = <strong>€{(itemPrice * itemQty).toFixed(2)}</strong>
                        {isSharedAssignment && (
                          <span style={{ color: '#7b1fa2', fontWeight: 700 }}> → €{sharedPrice.toFixed(2)}/person</span>
                        )}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: { xs: 130, sm: 155 } }}>
                      <Select
                        value={assignedPersonId}
                        onChange={(e) => assignItemToPerson(itemKey, e.target.value as string)}
                        displayEmpty
                        sx={{
                          borderRadius: 1.5, fontSize: '0.82rem',
                          '& .MuiSelect-select': { py: 0.8 },
                          ...(isSharedAssignment && { color: '#7b1fa2', fontWeight: 700 }),
                          ...(!isSharedAssignment && assignedPersonId && { color: getPersonColor(personIdx), fontWeight: 700 }),
                        }}
                      >
                        <MenuItem value=""><Typography sx={{ color: '#9ca3af', fontSize: '0.82rem' }}>{t('split.select_person')}</Typography></MenuItem>
                        {people.map((person, pIdx) => (
                          <MenuItem key={person.id} value={person.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 18, height: 18, background: getPersonColor(pIdx), fontSize: '0.6rem', fontWeight: 700 }}>
                                {person.name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{person.name}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Paper>
                );
              })}
              {effectiveItems.length === 0 && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>{t('split.no_items_available')}</Alert>
              )}
            </Box>
          </Box>
        );
      }

      case 2: {
        const paidCount = people.filter(p => p.paymentStatus === 'paid').length;
        return (
          <Box>
            <OrderBanner />

            {/* Status header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '0.95rem' }}>
                  {t('split.process_payments_title')}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  {paidCount}/{people.length} payments collected
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {people.map((p, idx) => (
                  <Tooltip key={p.id} title={`${p.name}: ${p.paymentStatus === 'paid' ? 'Paid' : 'Pending'}`}>
                    <Avatar sx={{
                      width: 30, height: 30, fontSize: '0.72rem', fontWeight: 700,
                      background: p.paymentStatus === 'paid' ? '#4caf50' : getPersonColor(idx),
                      border: p.paymentStatus === 'paid' ? '2px solid #2e7d32' : '2px solid transparent',
                      opacity: p.paymentStatus === 'paid' ? 1 : 0.5,
                    }}>
                      {p.paymentStatus === 'paid' ? '✓' : p.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              </Box>
            </Box>

            {people.map((person, personIdx) => {
              const isPaid = person.paymentStatus === 'paid';
              const itemTotal = (person.subtotal || 0) + (person.tax || 0);
              const finalAmount = itemTotal; // Always item-based — no custom amount override
              const personColor = getPersonColor(personIdx);
              return (
              <Paper key={person.id} elevation={0} sx={{
                mb: 2, borderRadius: 2, overflow: 'hidden',
                border: isPaid ? '2px solid #4caf50' : `2px solid ${personColor}30`,
                opacity: isPaid ? 0.85 : 1,
              }}>
                {/* Person header */}
                <Box sx={{
                  px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: isPaid ? 'rgba(76, 175, 80, 0.15)' : `linear-gradient(135deg,${personColor}12,${personColor}06)`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, background: isPaid ? '#4caf50' : personColor, fontWeight: 700 }}>
                      {isPaid ? <CheckCircleIcon sx={{ fontSize: 20 }} /> : person.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#e0e0e0', fontSize: '0.95rem' }}>{person.name}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#6b7280' }}>
                        {person.items.length} {t('split.items')} · €{(person.total || 0).toFixed(2)}
                      </Typography>
                      {person.items.length > 0 && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', mt: 0.2 }}>
                          {person.items.map(i => `${i.quantity > 1 ? `${i.quantity}× ` : ''}${i.name}`).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('split.final_amount')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: isPaid ? '#15803d' : personColor, lineHeight: 1 }}>
                      €{finalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                {!isPaid && (
                  <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                    {/* Amount breakdown */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                      <Chip label={`Netto €${(person.subtotal||0).toFixed(2)}`} size="small" sx={{ fontSize: '0.7rem', background: '#1e2139', fontWeight: 500 }} />
                      <Chip label={`MwSt. €${(person.tax||0).toFixed(2)}`} size="small" sx={{ fontSize: '0.7rem', background: '#1e2139', fontWeight: 500 }} />
                      <Chip label={`Brutto €${itemTotal.toFixed(2)}`} size="small" sx={{ fontSize: '0.7rem', background: '#e0f2fe', fontWeight: 700, color: '#0369a1' }} />
                    </Box>


                  {/* Payment method */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#d1d5db', mb: 0.8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {t('split.payment_method')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                      {[
                        { value: 'cash', label: 'Cash', icon: '💵' },
                        { value: 'card', label: 'Card', icon: '💳' },
                        { value: 'paypal', label: 'PayPal', icon: '🅿️' },
                        { value: 'apple_pay', label: 'Apple Pay', icon: '🍎' },
                        { value: 'google_pay', label: 'Google Pay', icon: '📱' },
                      ].map(method => {
                        const selected = (paymentMethods[person.id] || 'cash') === method.value;
                        return (
                          <Chip key={method.value}
                            label={`${method.icon} ${method.label}`}
                            size="small" clickable
                            onClick={() => setPaymentMethods(prev => ({ ...prev, [person.id]: method.value }))}
                            sx={{
                              fontSize: { xs: '0.82rem', sm: '0.75rem' },
                              height: { xs: 36, sm: 32 }, // Larger touch targets on tablets
                              fontWeight: selected ? 700 : 500,
                              background: selected ? personColor : '#f3f4f6',
                              color: selected ? '#fff' : '#374151',
                              border: `1.5px solid ${selected ? personColor : '#e5e7eb'}`,
                              '&:hover': { background: selected ? personColor : '#e5e7eb' },
                              touchAction: 'manipulation',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Tip */}
                  <Box sx={{ mb: 1.5 }}>
                    <FormControlLabel
                      control={<Switch size="small" checked={customTip} onChange={(e) => { setCustomTip(e.target.checked); if (!e.target.checked) setTipAmount(0); }} color="primary" />}
                      label={<Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#d1d5db' }}>{t('split.add_tip')}</Typography>}
                    />
                    {customTip && (
                      <TextField size="small" fullWidth label={t('split.tip_amount')} type="number" value={tipAmount}
                        onChange={(e) => setTipAmount(parseFloat(e.target.value) || 0)}
                        sx={{ mt: 0.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    )}
                  </Box>

                  {/* Balance check — always balanced since amounts come from assigned items */}
                  <Typography sx={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 600 }}>✓ {t('split.total_amount_balanced')}</Typography>

                  {/* Process button */}
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button variant="contained" startIcon={<PaymentIcon />}
                      onClick={() => processPayment(person.id)}
                      sx={{
                        flex: 1, borderRadius: 2, fontWeight: 700, textTransform: 'none',
                        py: { xs: 1.5, sm: 1.2 },
                        minHeight: 44, // Touch-friendly min height for tablets
                        fontSize: { xs: '0.9rem', sm: '0.875rem' },
                        background: `linear-gradient(135deg, ${personColor}, ${personColor}cc)`,
                        boxShadow: `0 3px 10px ${personColor}40`,
                        '&:hover': { background: personColor },
                        touchAction: 'manipulation', // Prevent double-tap zoom on tablets
                      }}
                    >
                      {t('split.collect')} €{(finalAmount + (tipAmount || 0)).toFixed(2)}
                    </Button>
                  </Box>
                </Box>
                )}

                {isPaid && (
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                      <Typography sx={{ fontWeight: 600, color: '#15803d', fontSize: '0.88rem' }}>
                        {t('split.paid')} · {paymentMethods[person.id] || 'cash'} · €{finalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    {person.items.length > 0 && (
                      <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', mt: 0.5, pl: 3.5 }}>
                        {person.items.map(i => `${i.quantity > 1 ? `${i.quantity}× ` : ''}${i.name}`).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
              );
            })}
          </Box>
        );
      }

      case 3: {
        const totalAmount = people.reduce((sum, p) => sum + (p.total || 0), 0);
        const avgPerPerson = people.length > 0 ? totalAmount / people.length : 0;
        return (
          <Box>
            {/* Two-action choice card */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box sx={{
                flex: 1, p: 2, border: '2px solid rgba(255, 152, 0, 0.3)', borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.06)',
                cursor: 'default',
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#e65100', mb: 0.5 }}>
                  Partial Pay &amp; Keep Table Open
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.4 }}>
                  These {people.length} guest{people.length !== 1 ? 's' : ''} paid and left.
                  Their items are deducted from the bill.
                  Table {tableNumber} stays <strong>open</strong> for remaining guests.
                </Typography>
              </Box>
              <Box sx={{
                flex: 1, p: 2, border: '2px solid rgba(0, 200, 150, 0.3)', borderRadius: 2, bgcolor: 'rgba(0, 200, 150, 0.06)',
                cursor: 'default',
              }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1b5e20', mb: 0.5 }}>
                  Complete Split &amp; Close Table
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.4 }}>
                  All guests have paid and left.
                  Table {tableNumber} is <strong>fully closed</strong>.
                  Use this when everyone is done.
                </Typography>
              </Box>
            </Box>

            {/* Success Banner */}
            <Box sx={{
              background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)',
              borderRadius: 3,
              p: 3,
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              color: 'white',
              boxShadow: '0 4px 20px rgba(46,125,50,0.3)',
            }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <CheckCircleIcon sx={{ fontSize: 32, color: '#a5d6a7' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5, color: '#ffffff !important' }}>
                  {t('split.complete_title')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.3, color: '#ffffff !important' }}>
                  {t('split.complete_success')}
                </Typography>
              </Box>
            </Box>

            {/* Summary Stats Row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: t('split.table'), value: tableNumber, icon: <TableRestaurantIcon sx={{ color: '#2e7d32' }} /> },
                { label: t('split.number_of_people'), value: people.length, icon: <PersonAddIcon sx={{ color: '#1565c0' }} /> },
                { label: t('split.total_amount'), value: `€${totalAmount.toFixed(2)}`, icon: <PaymentIcon sx={{ color: '#e65100' }} /> },
                { label: t('split.avg_per_person'), value: `€${avgPerPerson.toFixed(2)}`, icon: <AssignmentIcon sx={{ color: '#6a1b9a' }} /> },
              ].map((stat, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Paper elevation={0} sx={{
                    p: 2,
                    textAlign: 'center',
                    border: '1px solid #e8f5e9',
                    borderRadius: 2,
                    bgcolor: '#f9fbe7',
                  }}>
                    {stat.icon}
                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.1rem' }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Per-Person Receipt Cards */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#424242', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.72rem' }}>
              {t('split.number_of_people')} — {t('split.complete_title')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
              {people.map((person, idx) => {
                const color = getPersonColor(idx);
                return (
                  <Paper key={person.id} elevation={0} sx={{
                    p: 2,
                    border: `1.5px solid ${color}30`,
                    borderLeft: `4px solid ${color}`,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: `${color}08`,
                  }}>
                    <Avatar sx={{ bgcolor: color, width: 40, height: 40, fontWeight: 700, fontSize: '0.95rem', flexShrink: 0 }}>
                      {person.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{person.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {person.items.length} {person.items.length === 1 ? 'item' : 'items'}
                        {person.payments?.[0]?.paymentMethod ? ` · ${person.payments[0].paymentMethod}` : ''}
                      </Typography>
                      {person.items.length > 0 && (
                        <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', mt: 0.2, display: 'block' }}>
                          {person.items.map(i => `${i.quantity > 1 ? `${i.quantity}× ` : ''}${i.name}`).join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color }}>
                      €{(person.total || 0).toFixed(2)}
                    </Typography>
                    <Chip
                      icon={<CheckCircleOutlineIcon />}
                      label={person.paymentStatus === 'paid' ? 'Paid' : person.paymentStatus}
                      size="small"
                      color={person.paymentStatus === 'paid' ? 'success' : 'warning'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Paper>
                );
              })}
            </Box>

            {/* Print / Export Actions */}
            <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#1e2139' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ReceiptIcon sx={{ color: '#424242', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#212121' }}>
                  TSE-Compliant Receipts & Reports
                </Typography>
                <Chip label="TSE" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.65rem' }} />
              </Box>
              <Grid container spacing={1.5}>
                {[
                  { label: t('split.split_receipt'), icon: <ReceiptIcon fontSize="small" />, action: () => printReceipt('customer'), color: '#1565c0' },
                  { label: t('split.kitchen_order'), icon: <AssignmentIcon fontSize="small" />, action: () => printReceipt('kitchen'), color: '#e65100' },
                  { label: 'TSE Invoice', icon: <PrintIcon fontSize="small" />, action: () => printReceipt('tse'), color: '#6a1b9a' },
                  { label: t('split.export_csv'), icon: <DownloadIcon fontSize="small" />, action: () => exportReport(), color: '#00695c' },
                ].map((btn, i) => (
                  <Grid item xs={6} key={i}>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={btn.icon}
                      onClick={btn.action}
                      sx={{
                        borderColor: `${btn.color}40`,
                        color: btn.color,
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        py: 1,
                        '&:hover': { borderColor: btn.color, bgcolor: `${btn.color}08` },
                      }}
                    >
                      {btn.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        );
      }

      default:
        return 'Unknown step';
    }
  };

  // Handle close with confirmation if there's unsaved work
  const handleClose = () => {
    if (session && (people.length > 0 || activeStep > 0)) {
      // There's work in progress - show confirmation
      setShowCloseConfirm(true);
    } else {
      // No work - close immediately
      onClose();
    }
  };

  // Confirm close and save as draft
  const handleConfirmClose = async () => {
    if (session) {
      try {
        console.log('💾 [DRAFT] Saving draft before closing dialog...');
        // Save as draft before closing
        await saveDraftToFirestore();
        console.log('✅ [DRAFT] Draft saved successfully before closing');
      } catch (error) {
        console.error('❌ [DRAFT] Error saving draft before closing:', error);
        setSplitAlert({ open: true, title: 'Draft Error', message: t('split.save_draft_error') || 'Failed to save draft', icon: '⚠️', severity: 'error' });
        return; // Don't close if save failed
      }
    }
    setShowCloseConfirm(false);
    onClose();
  };

  // Cancel draft (delete from Firestore)
  const handleCancelDraft = async () => {
    if (session) {
      try {
        console.log('🗑️ [DRAFT] Cancelling draft...', session.id);
        const existingDraft = await draftService.getDraftBySessionId(session.id);
        if (existingDraft && existingDraft.id) {
          await draftService.cancelDraft(existingDraft.id);
          console.log('✅ [DRAFT] Draft cancelled/deleted successfully');
        }
      } catch (error) {
        console.error('❌ [DRAFT] Error cancelling draft:', error);
        // Don't block closing even if cancel fails
      }
    }
  };

  // Cancel close - just close the confirmation dialog, don't cancel draft
  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  // Delete draft and close
  const handleDeleteAndClose = async () => {
    await handleCancelDraft();
    setShowCloseConfirm(false);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Full Screen Modal - Same Style as Full Order Details */}
      <Box
        data-modal-overlay="true"
        onClick={(e) => {
          // Prevent closing by clicking overlay - require explicit cancel
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
        sx={{
          position: 'fixed',
          top: { xs: 56, sm: 0 },
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'center',
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'auto'
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            backgroundColor: '#0A2540',
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            border: { xs: 'none', sm: '1px solid rgba(46,125,50,0.2)' },
            maxWidth: { xs: '100%', sm: '680px', lg: '900px' },
            width: { xs: '100%', sm: '75%', lg: '90%' },
            maxHeight: { xs: '100%', sm: '80vh', lg: '90vh' },
            overflow: 'hidden',
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header — green gradient, compact on tablet */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 60%, #388e3c 100%)',
            px: { xs: 1.5, lg: 3 },
            py: { xs: 1, lg: 2 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: { xs: 28, lg: 36 },
                height: { xs: 28, lg: 36 },
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CallSplitIcon sx={{ color: '#a5d6a7', fontSize: { xs: 16, lg: 20 } }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', lineHeight: 1.2, fontSize: { xs: '0.9rem', lg: '1.25rem' }, textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                  {t('split.title')} — {tableNumber}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85) !important', fontSize: { xs: '0.65rem', lg: '0.75rem' }, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                  {steps[activeStep]}
                </Typography>
              </Box>
              {isSavingDraft && (
                <Chip
                  label={t('split.saving')}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            <IconButton
              onClick={async () => {
                await handleCancelDraft();
                onClose();
              }}
              size="small"
              sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', color: 'white' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Stepper Bar */}
          <Box sx={{ px: { xs: 1, sm: 1.5, lg: 3 }, pt: { xs: 0.5, lg: 2 }, pb: { xs: 0.25, lg: 1 }, borderBottom: '1px solid #2a2d3e' }}>
            <Stepper activeStep={activeStep} sx={{
              '& .MuiStepLabel-label': { fontSize: { xs: '0.55rem', sm: '0.65rem', lg: '0.8rem' }, fontWeight: 600, color: '#9ca3af !important', display: { xs: 'none', sm: 'block' } },
              '& .MuiStepLabel-label.Mui-active': { color: '#00C896 !important', display: 'block' },
              '& .MuiStepLabel-label.Mui-completed': { color: '#00C896 !important' },
              '& .MuiStepIcon-text': { fontSize: '0.7rem', fontWeight: 700 },
              '& .MuiStepIcon-root': { color: '#2a2d3e' },
              '& .MuiStepIcon-root.Mui-active': { color: '#00C896' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#00C896' },
              '& .MuiStepConnector-line': { borderColor: '#2a2d3e' },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#00C896' },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: '#00C896' },
            }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel
                    StepIconProps={{
                      sx: {
                        color: '#2a2d3e',
                        '&.Mui-active': { color: '#00C896' },
                        '&.Mui-completed': { color: '#00C896' },
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            {/* Mini progress bar */}
            <LinearProgress
              variant="determinate"
              value={(activeStep / (steps.length - 1)) * 100}
              sx={{
                mt: 1.5,
                height: 3,
                borderRadius: 2,
                bgcolor: '#2a2d3e',
                '& .MuiLinearProgress-bar': { bgcolor: '#00C896', borderRadius: 2 },
              }}
            />
          </Box>

          {/* Content */}
          <Box sx={{ p: { xs: 1.5, lg: 3 }, overflowY: 'auto', flex: 1, WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Footer Actions */}
          <Box sx={{
            px: { xs: 1.5, lg: 3 },
            py: { xs: 0.75, lg: 1.5 },
            borderTop: '1px solid #2a2d3e',
            bgcolor: '#1e2139',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
          }}>
            {/* Step indicator */}
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
              {t('split.step')} {activeStep + 1} / {steps.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                onClick={async () => {
                  await handleCancelDraft();
                  onClose();
                }}
                variant="outlined"
                size="small"
                sx={{ borderColor: '#3a3d52', color: '#9ca3af', minHeight: { xs: 28, lg: 40 }, fontSize: { xs: '0.68rem', lg: '0.8125rem' }, touchAction: 'manipulation', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' } }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
                size="small"
                sx={{ borderColor: '#3a3d52', color: '#9ca3af', minHeight: { xs: 28, lg: 40 }, fontSize: { xs: '0.68rem', lg: '0.8125rem' }, touchAction: 'manipulation', '&:hover': { bgcolor: '#252838', borderColor: '#00C896', color: '#00C896' } }}
              >
                {t('common.back')}
              </Button>
              {activeStep === steps.length - 1 ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={partialCheckout}
                    sx={{
                      borderColor: '#f59e0b',
                      color: '#f59e0b',
                      fontWeight: 700,
                      px: { xs: 1, lg: 2 },
                      minHeight: { xs: 28, lg: 40 },
                      fontSize: { xs: '0.65rem', lg: '0.8125rem' },
                      touchAction: 'manipulation',
                      '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.1)', borderColor: '#f59e0b' },
                    }}
                  >
                    {t('split.partial_pay') || 'Partial Pay'}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={completeSplit}
                    sx={{
                      bgcolor: '#00C896',
                      color: '#0A2540',
                      px: { xs: 1, lg: 2 },
                      fontWeight: 700,
                      minHeight: { xs: 28, lg: 40 },
                      fontSize: { xs: '0.68rem', lg: '0.8125rem' },
                      touchAction: 'manipulation',
                      boxShadow: '0 2px 8px rgba(0, 200, 150, 0.3)',
                      '&:hover': { bgcolor: '#1AD4A6' },
                    }}
                  >
                    {t('split.complete')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleNext}
                  disabled={
                    (activeStep === 0 && people.length === 0) ||
                    (activeStep === 2 && people.some(p => p.paymentStatus !== 'paid'))
                  }
                  sx={{
                    bgcolor: '#00C896',
                    color: '#0A2540',
                    px: { xs: 2, lg: 3 },
                    fontWeight: 700,
                    minHeight: { xs: 28, lg: 40 },
                    fontSize: { xs: '0.68rem', lg: '0.8125rem' },
                    touchAction: 'manipulation',
                    boxShadow: '0 2px 8px rgba(0, 200, 150, 0.3)',
                    '&:hover': { bgcolor: '#1AD4A6' },
                    '&.Mui-disabled': { bgcolor: '#2a2d3e', color: '#6b7280' },
                  }}
                >
                  {t('common.next')}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Close Confirmation Dialog */}
      <Dialog open={showCloseConfirm} onClose={handleCancelClose}>
        <DialogTitle>
          {t('split.close_split')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('split.close_split_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteAndClose}
            color="error"
          >
            {t('split.cancel_delete')}
          </Button>
          <Button onClick={handleCancelClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirmClose} variant="contained" color="primary">
            {t('split.save_draft_close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Centered alert dialog (replaces browser alert()) */}
      <Dialog
        open={Boolean(splitAlert?.open)}
        onClose={() => { if (splitAlert?.onClose) splitAlert.onClose(); else setSplitAlert(null); }}
        PaperProps={{
          sx: {
            borderRadius: 3, overflow: 'hidden', minWidth: 340, maxWidth: 420,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }
        }}
      >
        <Box sx={{
          background: splitAlert?.severity === 'success'
            ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
            : splitAlert?.severity === 'error'
              ? 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)'
              : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
          px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Typography sx={{ fontSize: 28 }}>{splitAlert?.icon || 'ℹ️'}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', fontSize: '1rem' }}>
            {splitAlert?.title || 'Notice'}
          </Typography>
        </Box>
        <Box sx={{ px: 3, py: 2.5, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ color: '#2d3748', fontWeight: 500, lineHeight: 1.6 }}>
            {splitAlert?.message}
          </Typography>
        </Box>
        <Box sx={{ px: 3, pb: 2.5, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => { if (splitAlert?.onClose) splitAlert.onClose(); else setSplitAlert(null); }}
            sx={{
              borderRadius: 2, px: 4, fontWeight: 700, textTransform: 'none',
              background: splitAlert?.severity === 'success'
                ? 'linear-gradient(135deg, #2e7d32, #1b5e20)'
                : splitAlert?.severity === 'error'
                  ? 'linear-gradient(135deg, #c62828, #b71c1c)'
                  : 'linear-gradient(135deg, #1565c0, #0d47a1)',
            }}
          >
            OK
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default SplitTableDialog;
